// Actions
import { ActionType } from "@ms/planner-actions/lib/local/ActionType";
import { PlannerDefaultPlanId } from "@ms/uno-constants/lib/local/ServiceConstants";
import { FlaggedEmailPlanId, StoreEntityState, StoreTableNames, TableName } from "@ms/uno-constants/lib/local/StoreConstants";
import { ViewName } from "@ms/uno-constants/lib/local/ViewConstants";
import { MyPlansPagePivotType } from "@ms/uno-constants/lib/local/ViewPivots";
import { applyFilter, registerIndex } from "@ms/uno-entitycore/lib/local/table/EntityTable";
import { Table } from "@ms/uno-entitycore/lib/local/table/Table";
// Models
import { OperationStatus } from "@ms/planner-models/lib/local/client/operations/Enumerations";
import { PlannerDefaultPlanIdKey } from "@ms/planner-models/lib/local/client/persistence/defaultPlan/IPlannerDefaultPlanIdRecord";
import { DefaultPlanSource } from "@ms/planner-models/lib/local/client/planner/DefaultPlanSource";
import { QueryRelationship } from "@ms/planner-models/lib/local/client/planner/EntityRelationships";
import { EntityType } from "@ms/planner-models/lib/local/client/planner/EntityType";
import { PlanMoveOperationStatus } from "@ms/planner-models/lib/local/client/planner/Enumerations";
import { ContainerType } from "@ms/planner-models/lib/local/service/planner/enumerations/IPlannerEnumerations";
// Stores
import { EventPublishingStore } from "@ms/uno-fluxcore/lib/local/stores/EventPublishingStore";
import { UpdatableSlice } from "../../slice/UpdatableSlice";
import { Store } from "../Store";
// Utilities
import { isEntityAction } from "@ms/planner-actions/lib/local/utils/ActionUtils";
import { toViewIdString } from "@ms/uno-utilities/lib/local/ViewUtilities";
import { Temporal } from "temporal-polyfill-lite";
import { logUnreachableAction } from "../../utilities/LoggingUtilities";
import { AbsentStoreEntityStatus } from "../../utilities/StatusUtilities";
import { filterPersistedTablesForStore } from "../../utilities/StoreUtilities";
// Plan store indexes
var PlanIndex = /*#__PURE__*/ function(PlanIndex) {
    PlanIndex["ContainerType"] = "containerType";
    PlanIndex["ContainerId"] = "containerId";
    PlanIndex["View"] = "view";
    return PlanIndex;
}(PlanIndex || {});
// Recommended plan set constants
const RecommendedPlanSetId = "recommended";
// View partition keys
const MyPlansViewPartition = "MyPlansView";
const MyPlansViewPivotIndex = "myPlansViewPivot";
export class PlanStore extends EventPublishingStore {
    get name() {
        return Store.Plan;
    }
    getPersistedTables() {
        return filterPersistedTablesForStore(Store.Plan, {
            [TableName.Plan]: this.planSlice.table(),
            [TableName.PlannerDefaultPlanId]: this.plannerDefaultPlanIdTable,
            ...this.planSlice.statusTables()
        });
    }
    getPlan(id, readBaseline) {
        return this.planSlice.get(this.resolvePlanId(id), readBaseline);
    }
    getPlansForContainer(containerType, containerId) {
        return this.planSlice.getCollection({
            name: "containerId",
            value: toContainerIdString(containerType, containerId)
        });
    }
    getPlansForContainerType(containerType) {
        return this.planSlice.getCollection({
            name: "containerType",
            value: containerType
        });
    }
    getPlanMoveOperationStatus(planId) {
        return this.movePlanOperationStatusTable.get(planId);
    }
    getPlanLinkOperation(sourceTaskId) {
        return this.planLinkOperationTable.get(sourceTaskId);
    }
    getCreatePlanFromTemplateOperationStatus(planId) {
        return this.createPlanFromTemplateOperationStatusTable.get(planId);
    }
    getExtendPlanExpiryOperationStatus(planId) {
        return this.extendPlanExpiryOperationStatusTable.get(planId);
    }
    getDefaultPlanId(source) {
        return this.defaultPlanIds[source] ?? null;
    }
    isDefaultPlan(planId) {
        return this.getDefaultPlanSource(planId) != null;
    }
    getDefaultPlanSource(planId) {
        if (planId === PlannerDefaultPlanId) {
            return this.activeDefaultPlanSource;
        }
        return Object.values(DefaultPlanSource).find((source)=>this.defaultPlanIds[source] === planId) ?? null;
    }
    getDefaultPlanStatus(source) {
        return this.defaultPlanStatuses[source] ?? AbsentStoreEntityStatus;
    }
    hasDefaultPlan() {
        return this.activeDefaultPlanSource === DefaultPlanSource.Planner || this.getDefaultPlanId(DefaultPlanSource.ToDo) != null;
    }
    getFlaggedEmailPlanId() {
        return this.specialPlansTable.get(FlaggedEmailPlanId)?.[0] ?? null;
    }
    getRecommendedPlanIds() {
        return this.specialPlansTable.get(RecommendedPlanSetId) ?? [];
    }
    getRosterPlansNextLink() {
        return this.rosterPlansNextLink;
    }
    getPlansForView(viewId) {
        switch(viewId.name){
            case ViewName.MyPlansPage:
                return this.getMyPlansViewPartition().index(MyPlansViewPivotIndex).partition(viewId.partitionKey).items().map((item)=>item.value);
            default:
                throw new Error(`PlanPartitionNotFound [View=${toViewIdString(viewId)}]`);
        }
    }
    getBaselineChainPair(id, deltaKey) {
        return this.planSlice.getBaselineChainPair(id, deltaKey);
    }
    handleAction(action) {
        let storeChanged = false;
        if (!this.shouldHandleAction(action)) {
            return;
        }
        switch(action.type){
            case ActionType.FetchEntityAction:
                storeChanged = this.planSlice.handleFetchStarted(action.entityId);
                break;
            case ActionType.FetchEntityFailedAction:
                storeChanged = this.planSlice.handleFetchFailed(action.entityId, action.response);
                break;
            case ActionType.FetchEntitySucceededAction:
                storeChanged = this.planSlice.handleFetchSucceeded(action.entity.id, action.entity, action.forceOverrideStoredData);
                break;
            case ActionType.FetchEntityCollectionAction:
                if (Object.values(ContainerType).includes(action.parentType)) {
                    storeChanged = this.planSlice.handleCollectionFetchStarted({
                        name: "containerId",
                        value: toContainerIdString(action.parentType, action.parentId)
                    });
                }
                break;
            case ActionType.FetchEntityCollectionFailedAction:
                if (Object.values(ContainerType).includes(action.parentType)) {
                    storeChanged = this.planSlice.handleCollectionFetchFailed({
                        name: "containerId",
                        value: toContainerIdString(action.parentType, action.parentId)
                    }, action.response);
                }
                break;
            case ActionType.FetchEntityCollectionSucceededAction:
                if (action.relationship === QueryRelationship.RosterPlansForUser) {
                    // User-level roster query carries the pagination cursor for "Show more".
                    // The plans themselves are persisted via per-container PlansInContainer successes.
                    this.rosterPlansNextLink = action.nextLink ?? null;
                    storeChanged = true;
                } else if (Object.values(ContainerType).includes(action.parentType)) {
                    storeChanged = this.planSlice.handleCollectionFetchSucceeded({
                        name: "containerId",
                        value: toContainerIdString(action.parentType, action.parentId)
                    }, true, Object.fromEntries(action.entities.map((item)=>[
                            item.id,
                            item
                        ])), [], action.deltaLink, action.forceOverrideStoredData);
                }
                break;
            case ActionType.FetchAllPlansForContainerTypeAction:
                storeChanged = this.planSlice.handleCollectionFetchStarted({
                    name: "containerType",
                    value: action.containerType
                });
                break;
            case ActionType.FetchAllPlansForContainerTypeFailedAction:
                storeChanged = this.planSlice.handleCollectionFetchFailed({
                    name: "containerType",
                    value: action.containerType
                }, action.response);
                break;
            case ActionType.FetchAllPlansForContainerTypeSucceededAction:
                const storePlansArray = this.planSlice.getCollection({
                    name: "containerType",
                    value: action.containerType
                }).data;
                storeChanged = this.planSlice.handleCollectionFetchSucceeded({
                    name: "containerType",
                    value: action.containerType
                }, action.isLastPage, Object.fromEntries(storePlansArray.map((item)=>[
                        item.id,
                        item
                    ])), []);
                break;
            case ActionType.CreateEntityAction:
                storeChanged = this.planSlice.handleCreateStarted(action.entity.id, action.entity);
                break;
            case ActionType.CreateEntityFailedAction:
                storeChanged = this.planSlice.handleCreateFailed(action.entity.id);
                break;
            case ActionType.CreateEntitySucceededAction:
                storeChanged = this.planSlice.handleCreateSucceeded(action.localId, action.entity, action.entity.id);
                break;
            case ActionType.DeleteEntityAction:
                storeChanged = this.planSlice.handleDeleteStarted(action.entityId);
                break;
            case ActionType.DeleteEntityFailedAction:
                storeChanged = this.planSlice.handleDeleteFailed(action.entityId, action.response, action.errorCode);
                break;
            case ActionType.DeleteEntitySucceededAction:
                storeChanged = this.planSlice.handleDeleteSucceeded(action.entityId);
                break;
            case ActionType.UpdateEntityAction:
                storeChanged = this.planSlice.handleUpdateStarted(action.entityId, action.updateKey, action.delta);
                break;
            case ActionType.UpdateEntityFailedAction:
                storeChanged = this.planSlice.handleUpdateFailed(action.entityId, action.updateKey, action.response, action.errorCode);
                break;
            case ActionType.UpdateEntitySucceededAction:
                storeChanged = this.planSlice.handleUpdateSucceeded(action.entity.id, action.updateKey, action.entity);
                break;
            case ActionType.UndoRedoUpdateEntitySucceededAction:
                storeChanged = this.planSlice.handleUpdateSucceeded(action.entity.id, action.updateKey, action.entity, true);
                break;
            case ActionType.SetDefaultPlanAction:
                storeChanged = this.setDefaultPlanId(action.source, action.value);
                break;
            case ActionType.ClearDefaultPlanAction:
                storeChanged = this.clearDefaultPlanId(action.source);
                break;
            case ActionType.FetchDefaultPlanAction:
                storeChanged = this.setDefaultPlanStatus(action.source, true, StoreEntityState.Absent);
                break;
            case ActionType.FetchDefaultPlanFailedAction:
                storeChanged = this.setDefaultPlanStatus(action.source, false, StoreEntityState.UnableToFetch);
                break;
            case ActionType.SetFlaggedEmailPlanAction:
                storeChanged = this.setSpecialPlanIds(FlaggedEmailPlanId, [
                    action.value
                ]);
                break;
            case ActionType.StartPlanLinkOperationAction:
                storeChanged = this.handleStartPlanLinkOperationAction(action);
                break;
            case ActionType.CompletePlanLinkOperationAction:
                storeChanged = this.handleCompletePlanLinkOperationAction(action);
                break;
            case ActionType.MovePlanOperationAction:
                storeChanged = this.handleMovePlanOperationAction(action);
                break;
            case ActionType.MovePlanOperationCompletedAction:
                storeChanged = this.handleMovePlanOperationCompletedAction(action);
                break;
            case ActionType.MovePlanOperationCompletedWithErrorsAction:
                storeChanged = this.handleMovePlanOperationCompletedWithErrorsAction(action);
                break;
            case ActionType.ResetMovePlanOperationAction:
                storeChanged = this.handleResetMovePlanOperationAction(action);
                break;
            case ActionType.ExtendPlanExpiryOperationAction:
                storeChanged = this.handleExtendPlanExpiryOperationAction(action);
                break;
            case ActionType.ExtendPlanExpiryOperationCompletedAction:
                storeChanged = this.handleExtendPlanExpiryOperationCompletedAction(action);
                break;
            case ActionType.ExtendPlanExpiryOperationCompletedWithErrorsAction:
                storeChanged = this.handleExtendPlanExpiryOperationCompletedWithErrorsAction(action);
                break;
            case ActionType.ResetExtendPlanExpiryOperationAction:
                storeChanged = this.handleResetExtendPlanExpiryOperationAction(action);
                break;
            case ActionType.SetPlanFilterAction:
                storeChanged = this.handleSetPlanFilterAction(action);
                break;
            case ActionType.SetRecommendedPlansAction:
                storeChanged = this.setSpecialPlanIds(RecommendedPlanSetId, action.planIds);
                break;
            case ActionType.DeltaSyncAction:
                storeChanged = this.planSlice.handleDeltaSyncStarted({
                    name: "containerId",
                    value: toContainerIdString(action.parentType, action.parentId)
                });
                break;
            case ActionType.DeltaSyncSucceededAction:
                storeChanged = this.planSlice.handleDeltaSyncSucceeded({
                    name: "containerId",
                    value: toContainerIdString(action.parentType, action.parentId)
                }, Object.fromEntries(action.deltas.map((item)=>[
                        item.entity.id,
                        item
                    ])), action.deltaLink);
                break;
            case ActionType.DeltaSyncFailedAction:
                storeChanged = this.planSlice.handleDeltaSyncFailed({
                    name: "containerId",
                    value: toContainerIdString(action.parentType, action.parentId)
                }, action.response, action.errorCode);
                break;
            case ActionType.CreatePlanFromTemplateAction:
                storeChanged = this.handleCreatePlanFromTemplateAction(action);
                break;
            case ActionType.CreatePlanFromTemplateSucceededAction:
                storeChanged = this.handleCreatePlanFromTemplateSucceededAction(action);
                break;
            case ActionType.CreatePlanFromTemplateFailedAction:
                storeChanged = this.handleCreatePlanFromTemplateFailedAction(action);
                break;
            case ActionType.CreatePlanFromTemplateResetAction:
                storeChanged = this.handleCreatePlanFromTemplateResetAction(action);
                break;
            case ActionType.SuspendEntityDeltaAction:
                storeChanged = this.planSlice.handleSuspendDelta(action.entityId, action.deltaKey);
                break;
            case ActionType.ResumeEntityDeltaAction:
                storeChanged = this.planSlice.handleResumeDelta(action.entityId, action.deltaKey);
                break;
            case ActionType.DropSuspendedDeltaAction:
                storeChanged = this.planSlice.handleDropSuspendedDelta(action.entityId, action.deltaKey);
                break;
            case ActionType.PruneBaselineChainAction:
                storeChanged = this.planSlice.removeBaselineChainEntry(action.entityId, action.deltaKey, action.prunePrefix);
                break;
            default:
                logUnreachableAction(this, this.loggers, action);
                return;
        }
        if (storeChanged) {
            this.publishStoreChangedEvent();
        }
    }
    handleMovePlanOperationAction(action) {
        return this.movePlanOperationStatusTable.set(action.planId, PlanMoveOperationStatus.InProgress);
    }
    handleMovePlanOperationCompletedAction(action) {
        return this.movePlanOperationStatusTable.set(action.planId, PlanMoveOperationStatus.Completed);
    }
    handleMovePlanOperationCompletedWithErrorsAction(action) {
        return this.movePlanOperationStatusTable.set(action.planId, PlanMoveOperationStatus.CompletedWithErrors);
    }
    handleResetMovePlanOperationAction(action) {
        return this.movePlanOperationStatusTable.set(action.planId, null);
    }
    handleStartPlanLinkOperationAction(action) {
        const currentOperation = this.planLinkOperationTable.get(action.sourceTaskId);
        if (currentOperation != null) {
            // Keep the first operation so another create/connect cannot take ownership while it is running.
            return false;
        }
        return this.planLinkOperationTable.set(action.sourceTaskId, {
            operationId: action.operationId,
            kind: action.kind
        });
    }
    handleCompletePlanLinkOperationAction(action) {
        const currentOperation = this.planLinkOperationTable.get(action.sourceTaskId);
        return currentOperation?.operationId === action.operationId && currentOperation.kind === action.kind ? this.planLinkOperationTable.delete(action.sourceTaskId) : false;
    }
    handleExtendPlanExpiryOperationAction(action) {
        return this.extendPlanExpiryOperationStatusTable.set(action.planId, OperationStatus.Running);
    }
    handleExtendPlanExpiryOperationCompletedAction(action) {
        return this.extendPlanExpiryOperationStatusTable.set(action.planId, OperationStatus.CompletedSuccessfully);
    }
    handleExtendPlanExpiryOperationCompletedWithErrorsAction(action) {
        return this.extendPlanExpiryOperationStatusTable.set(action.planId, OperationStatus.CompletedWithErrors);
    }
    handleResetExtendPlanExpiryOperationAction(action) {
        return this.extendPlanExpiryOperationStatusTable.set(action.planId, null);
    }
    /**
     * Handles the create plan from template action and sets the operation status to running
     * @param action The create plan from template action
     */ handleCreatePlanFromTemplateAction(action) {
        return this.createPlanFromTemplateOperationStatusTable.set(action.planId, OperationStatus.Running);
    }
    /**
     * Handles the create plan from template succeeded action and sets the operation status to completed successfully
     * @param action The create plan from template succeeded action
     */ handleCreatePlanFromTemplateSucceededAction(action) {
        return this.createPlanFromTemplateOperationStatusTable.set(action.planId, OperationStatus.CompletedSuccessfully);
    }
    /**
     * Handles the create plan from template failed action and sets the operation status to completed with errors
     * @param action The create plan from template failed action
     */ handleCreatePlanFromTemplateFailedAction(action) {
        return this.createPlanFromTemplateOperationStatusTable.set(action.planId, OperationStatus.CompletedWithErrors);
    }
    /**
     * Handles the create plan from template reset action and resets the operation status
     * @param action The create plan from template reset action
     */ handleCreatePlanFromTemplateResetAction(action) {
        return this.createPlanFromTemplateOperationStatusTable.set(action.planId, null);
    }
    /**
     * Handle application of plan filter on the view
     */ handleSetPlanFilterAction(action) {
        const keyword = `${action.filter?.filters?.find((filter)=>{
            return filter.field === "name";
        })?.values?.[0] ?? ""}`.toLocaleLowerCase();
        applyFilter(this.getMyPlansViewPartition(), (plan)=>keyword.length > 0 ? plan.displayName.toLocaleLowerCase().indexOf(keyword) !== -1 : true);
        return true;
    }
    /**
     * Helper to set a special plan in store
     */ setSpecialPlanIds(key, planIds) {
        const storeChanged = this.specialPlansTable.set(key, planIds);
        // Refresh the applied filter for specified pivots
        if (storeChanged) {
            // Pivot membership can change in response to updates to known plan id's, hence index needs to be refreshed
            this.getMyPlansViewPartition().refreshIndex(MyPlansViewPivotIndex);
        }
        return storeChanged;
    }
    setDefaultPlanId(source, planId) {
        const identityChanged = this.defaultPlanIds[source] !== planId;
        const statusChanged = this.defaultPlanStatuses[source]?.isFetching !== false || this.defaultPlanStatuses[source]?.state !== StoreEntityState.Present;
        this.defaultPlanIds[source] = planId;
        if (source === DefaultPlanSource.Planner) {
            this.plannerDefaultPlanIdTable.set(PlannerDefaultPlanIdKey, {
                key: PlannerDefaultPlanIdKey,
                planId
            });
        }
        this.defaultPlanStatuses[source] = {
            isFetching: false,
            state: StoreEntityState.Present,
            timestamp: Temporal.Now.instant()
        };
        if (identityChanged) {
            this.getMyPlansViewPartition().refreshIndex(MyPlansViewPivotIndex);
        }
        return identityChanged || statusChanged;
    }
    clearDefaultPlanId(source) {
        // Stale-ID recovery broadcasts this action to stores that may already be clear, so deletion is intentionally idempotent.
        const identityChanged = this.defaultPlanIds[source] !== undefined;
        const statusChanged = this.defaultPlanStatuses[source] !== undefined;
        delete this.defaultPlanIds[source];
        delete this.defaultPlanStatuses[source];
        if (source === DefaultPlanSource.Planner) {
            this.plannerDefaultPlanIdTable.delete(PlannerDefaultPlanIdKey);
        }
        if (identityChanged) {
            this.getMyPlansViewPartition().refreshIndex(MyPlansViewPivotIndex);
        }
        return identityChanged || statusChanged;
    }
    setDefaultPlanStatus(source, isFetching, state) {
        const current = this.defaultPlanStatuses[source];
        if (current?.isFetching === isFetching && current.state === state) {
            return false;
        }
        this.defaultPlanStatuses[source] = {
            isFetching,
            state,
            timestamp: Temporal.Now.instant()
        };
        return true;
    }
    // Helper to get MyPlans view pivots for a plan
    getMyPlansViewPivotsForPlan(plan) {
        const pivots = [];
        // Case 1: Known plans do not show up in any pivot
        if (this.isDefaultPlan(plan.id) || plan.id === this.getFlaggedEmailPlanId()) {
            return pivots;
        }
        // Case 2: If a plan is in user container or is a personal (no sharing link) todo list, it should show up in personal pivot, shared otherwise
        if (plan.container.containerType === ContainerType.User || plan.container.containerType === ContainerType.ToDo && !plan.extensions.todo?.sharingLink) {
            pivots.push(MyPlansPagePivotType.Personal);
        } else {
            pivots.push(MyPlansPagePivotType.Shared);
        }
        // Case 3: If plan is in the recommended set, show in the recommended pivot
        if (this.getRecommendedPlanIds().includes(plan.id)) {
            pivots.push(MyPlansPagePivotType.Recommended);
        }
        // Case 4: All plans should show up in recent pivot (this is esp. needed once we fetch all user accessible plans)
        pivots.push(MyPlansPagePivotType.Recent);
        return pivots;
    }
    // Helper to get the partition for MyPlans view
    getMyPlansViewPartition() {
        return this.planTable.index("view").partition(MyPlansViewPartition);
    }
    shouldHandleAction(action) {
        if (isEntityAction(action)) {
            // Only handle entity actions for plans
            return action.entityType === EntityType.Plan;
        }
        return true; // Handle all other actions
    }
    /**
     * Maps the well-known Planner Default Plan id onto the canonical id of the plan record it refers to, so callers
     * can read the default plan without first resolving it themselves. Before the default plan is known the id is
     * left as-is, which reads as absent - the same result any unhydrated plan id gives.
     */ resolvePlanId(id) {
        return id === PlannerDefaultPlanId ? this.defaultPlanIds[this.activeDefaultPlanSource] ?? id : id;
    }
    /**
     * The default-plan source that currently backs the user's Private Tasks. Private Tasks is dual-source and the
     * `EnableMyTasksDefaultPlan` flight is the single decision point between them, so it is resolved here rather than
     * by each caller - otherwise the grid, board, and grouping code can disagree about which plan Private Tasks is.
     */ get activeDefaultPlanSource() {
        return this.configProvider().flights.EnableMyTasksDefaultPlan ? DefaultPlanSource.Planner : DefaultPlanSource.ToDo;
    }
    // Helper to get the plan table from the slice
    get planTable() {
        return this.planSlice.table();
    }
    constructor(loggers, configProvider){
        super(), this.loggers = loggers, this.configProvider = configProvider, /**
     * Plan table partition structure
     *
     * ```
     * ☶ Root Table: All plans
     * ├── ContainerType
     * │   ├── ☶ User
     * │   ├── ☶ Group
     * │   └── ☶ DriveItem
     * │
     * ├── ContainerId
     * │   ├── ☶ Group:G1
     * │   ├── ☶ Group:G1
     * │   ├── ☶ User:U1
     * │   ├── ☶ DriveItem:D1
     * │   ├── ☶ Roster:R1
     * │   └── ☶ Roster:R2
     * │
     * └── View
     *     └── ☶ MyPlansView
     *         └── Pivot
     *             ├── ☶ Personal
     *             ├── ☶ Shared
     *             └── ☶ Recommended
     * ```
     */ this.planSlice = new UpdatableSlice(TableName.Plan, {
            /** An index with a single partition per container type  */ ["containerType"]: (plan)=>plan.container.containerType,
            /** An index with a single partition per container id  */ ["containerId"]: (plan)=>toContainerIdString(plan.container.containerType, plan.container.containerId),
            /** An index for serving view queries (currently only MyPlansView)  */ ["view"]: (plan)=>MyPlansViewPartition
        }, (baseline, incoming)=>baseline.version < incoming.version || baseline.version === incoming.version && baseline.planView.conditionalColoringRules === undefined && incoming.planView.conditionalColoringRules !== undefined, this.configProvider), // Special plans
        this.specialPlansTable = new Table("specialPlans", (a, b)=>{
            // For arrays with single value, compare the value
            if (a.length === 1 && b.length === 1) {
                return a[0] === b[0];
            }
            // For multiple values, always assume they are different for simplicity
            return false;
        }), this.defaultPlanIds = {}, this.defaultPlanStatuses = {}, this.plannerDefaultPlanIdTable = new Table(TableName.PlannerDefaultPlanId), this.movePlanOperationStatusTable = new Table(StoreTableNames.MoveBasicPlanOperationStatus), this.planLinkOperationTable = new Table(StoreTableNames.PlanLinkOperation), this.createPlanFromTemplateOperationStatusTable = new Table(StoreTableNames.CreatePlanFromTemplateOperationStatus), this.extendPlanExpiryOperationStatusTable = new Table(StoreTableNames.ExtendPlanExpiryOperationStatus), /**
     * Roster plans pagination cursor. Holds the user-level `nextLink` returned by the most recent
     * roster fetch, or null when there are no more pages to fetch.
     */ this.rosterPlansNextLink = null;
        // Register an index on the MyPlansView partition for view pivots so that we have a partition for each pivot
        registerIndex(this.getMyPlansViewPartition(), MyPlansViewPivotIndex, (plan)=>this.getMyPlansViewPivotsForPlan(plan));
    }
}
/** Helper to convert container type and ID to a string */ function toContainerIdString(containerType, containerId) {
    return `${containerType}-${containerId}`;
}
